import { useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { ROUTES } from '../constants';
import { useConfigDataFetcher } from '../hooks/useConfigDataFetcher';
import { useAuth } from '../services/auth0';
import { tempRedirectInfoState } from '../store/global.store';

export const useAuthRouteInterceptor = () => {
  const {
    isAuthenticated,
    user,
    isLoading,
    accessToken,
    tenantId,
    userId,
    getTenantIdsFromAccessToken,
    currentTenant,
    currentTenantUser,
    isTokenRefreshed,
  } = useAuth();
  const setRedirectInfo = useSetAtom(tempRedirectInfoState);
  const [isReady, setIsReady] = useState(false);
  const [nextRoute, setNextRoute] = useState(ROUTES.ROOT);
  const { isConfigLoading } = useConfigDataFetcher({
    enabled:
      isAuthenticated && !!currentTenant && !!userId && !!user?.email_verified,
  });

  useEffect(() => {
    if (typeof getTenantIdsFromAccessToken !== 'function') {
      return;
    }
    const tenantIds = getTenantIdsFromAccessToken();

    if (user && !user.email_verified) {
      setIsReady(true);
      setNextRoute(ROUTES.ONBOARD_VERIFY_EMAIL);
    } else if (!userId) {
      setIsReady(true);
      setNextRoute(ROUTES.ONBOARD_CREATE_USER);
    } else if (tenantIds.length === 0) {
      setIsReady(true);
      setNextRoute(ROUTES.ONBOARD_NO_TENANT_ACCESS);
    } else if (tenantIds.length > 0 && !currentTenant) {
      setIsReady(true);
      setNextRoute(ROUTES.ONBOARD_SELECT_TENANT);
    } else if (isTokenRefreshed) {
      setIsReady(true);
      setNextRoute(ROUTES.ROOT);
    }
  }, [user, accessToken, tenantId, isAuthenticated, userId, isTokenRefreshed]);

  // Save the current URL to redirect back after login. This runs as an effect
  // rather than during render; it still lands before the logout route clears
  // storage, and clearLocalStorage() preserves the redirect info regardless.
  useEffect(() => {
    if (isAuthenticated || isLoading) {
      return;
    }
    const currentPath = `${window.location.pathname}${window.location.search}`;
    // Only save if we're not already on auth/login routes
    if (!currentPath.startsWith('/auth/')) {
      setRedirectInfo({
        tenantId: tenantId || undefined,
        redirectUrl: currentPath,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  // After HMR locally, the context is lost, which was forcing a logout on every code change
  if (typeof getTenantIdsFromAccessToken !== 'function') {
    return {
      isReady: false,
    };
  }

  if (!isAuthenticated) {
    return {
      isReady: true,
      nextRoute: ROUTES.AUTH_LOGOUT,
    };
  }

  // If tenant user is not loaded, block moving to next routes
  if (!currentTenantUser && tenantId && userId) {
    return {
      isReady: false,
    };
  }

  // If tenantId is valid and config is not loaded, show loading page.
  if (tenantId && isConfigLoading) {
    return {
      isReady: false,
    };
  }

  if (isLoading || !isTokenRefreshed) {
    return {
      isReady: false,
    };
  }

  return {
    isReady,
    nextRoute,
  };
};
